import { useState, useEffect, useMemo } from 'react';
import {
  doc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { dataManagementDoc, dataManagementSubcollection } from '../../constants/firestorePaths';
import {
  gateRefusalMessage,
  type DdDownloadResponseLike,
} from '../../features/verification/ddGateMessages';
import { useToast } from '../../contexts/ToastContext';
import { saveAs } from 'file-saver';
import { Loader2, Play, Download, FileText, RefreshCw } from 'lucide-react';
import { EditCheckSpec } from '../../constants/editCheckOptions';
import EditCheckFilters from './EditCheckFilters';
import EditCheckTable from './EditCheckTable';
import { specSummaryOf } from '../../features/editChecks/specSummary';
import {
  defaultRangesText,
  dictionarySummaryText,
  matchesSourceFilter,
  notWrittenLine,
  type DefaultRangesSummary,
  type DictionaryChecksSummary,
} from '../../features/editChecks/dictionaryChecksDisplay';
import {
  excludedFormLine,
  familyGapLine,
  notWrittenSummary,
  rejectedCheckLine,
  splitFamilyGaps,
  type NotWrittenFields,
} from '../../features/editChecks/notWrittenDisplay';

interface SpecMetadata extends NotWrittenFields {
  version: string;
  status: string;
  totalChecks: number;
  categoryCounts: Record<string, number>;
  generatedAt: any;
  /** What the uploaded dictionary contributed (D7). */
  dictionaryChecks?: DictionaryChecksSummary;
  /** Default plausibility ranges written, and numeric fields left without one. */
  defaultRanges?: DefaultRangesSummary;
}

interface EditCheckSpecPanelProps {
  projectId: string;
}

export default function EditCheckSpecPanel({ projectId }: EditCheckSpecPanelProps) {
  const [specData, setSpecData] = useState<SpecMetadata | null>(null);
  const [checks, setChecks] = useState<EditCheckSpec[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [exporting, setExporting] = useState<'csv' | 'docx' | null>(null);
  const { showToast } = useToast();

  // Filter state
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [timingFilter, setTimingFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  // Listen to edit check spec metadata
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'editCheckSpec'),
      (snapshot) => {
        if (snapshot.exists()) {
          setSpecData(snapshot.data() as SpecMetadata);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading edit check spec:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  // Listen to individual checks subcollection
  useEffect(() => {
    if (!projectId || !specData) return;

    const checksRef = dataManagementSubcollection(projectId, 'editCheckSpec', 'checks');

    const unsubscribe = onSnapshot(
      query(checksRef, orderBy('id')),
      (snapshot) => {
        const items: EditCheckSpec[] = [];
        snapshot.docs.forEach((d) => {
          items.push(d.data() as EditCheckSpec);
        });
        setChecks(items);
      },
      (err) => {
        console.error('Error loading edit checks:', err);
      }
    );

    return () => unsubscribe();
  }, [projectId, specData]);

  // Filter checks
  const filteredChecks = useMemo(() => {
    let result = checks;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) =>
          c.id.toLowerCase().includes(q) ||
          c.name.toLowerCase().includes(q) ||
          c.crfForm.toLowerCase().includes(q) ||
          c.fieldName.toLowerCase().includes(q) ||
          c.fieldLabel.toLowerCase().includes(q) ||
          c.rule.toLowerCase().includes(q) ||
          c.queryMessage.toLowerCase().includes(q)
      );
    }

    if (categoryFilter) {
      result = result.filter((c) => c.category === categoryFilter);
    }
    if (severityFilter) {
      result = result.filter((c) => c.severity === severityFilter);
    }
    if (sourceFilter) {
      result = result.filter((c) => matchesSourceFilter(c, sourceFilter));
    }
    if (timingFilter) {
      result = result.filter((c) => c.timing === timingFilter);
    }
    if (activeFilter === 'active') {
      result = result.filter((c) => c.active);
    } else if (activeFilter === 'inactive') {
      result = result.filter((c) => !c.active);
    }

    return result;
  }, [checks, search, categoryFilter, severityFilter, timingFilter, activeFilter, sourceFilter]);

  // Generate edit check spec
  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationProgress('Preparing specification...');

    const progressMessages = [
      'Analyzing CRF fields and protocol requirements...',
      'Generating endpoint checks...',
      'Generating safety checks...',
      'Generating compliance checks...',
      'Writing checks from your data dictionary...',
      'Finalizing specification...',
    ];
    let step = 0;
    const progressTimer = setInterval(() => {
      step++;
      if (step < progressMessages.length) {
        setGenerationProgress(progressMessages[step]);
      }
    }, 12000);

    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_EDIT_CHECK_SPECIFICATION_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      await generateFn({ projectId });
    } catch (err: any) {
      console.error('Edit check spec generation error:', err);
      showToast(err.message || 'Failed to generate edit check specification', 'error');
    } finally {
      clearInterval(progressTimer);
      setGenerating(false);
      setGenerationProgress('');
    }
  };

  // Export CSV
  const handleExportCsv = async () => {
    setExporting('csv');
    try {
      const exportFn = httpsCallable(functions, CLOUD_FUNCTIONS.EXPORT_EDIT_CHECK_SPEC_CSV, {
        // The gate may run a verification pass inline before serving (inc5 G).
        timeout: CALLABLE_TIMEOUTS.MEDIUM,
      });
      const result = await exportFn({ projectId });
      const refusal = gateRefusalMessage('edit check specification', result.data as DdDownloadResponseLike);
      if (refusal) {
        showToast(refusal, 'error');
        return;
      }
      const data = result.data as { csv: string; filename: string };

      const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, data.filename);
    } catch (err: any) {
      console.error('CSV export error:', err);
      showToast(err.message || 'Failed to export CSV', 'error');
    } finally {
      setExporting(null);
    }
  };

  // Export DOCX
  const handleExportDocx = async () => {
    setExporting('docx');
    try {
      const exportFn = httpsCallable(functions, CLOUD_FUNCTIONS.EXPORT_EDIT_CHECK_SPEC_DOCX, {
        // The gate may run a verification pass inline before serving (inc5 G).
        timeout: CALLABLE_TIMEOUTS.MEDIUM,
      });
      const result = await exportFn({ projectId });
      const refusal = gateRefusalMessage('edit check specification', result.data as DdDownloadResponseLike);
      if (refusal) {
        showToast(refusal, 'error');
        return;
      }
      const data = result.data as { docx: string; filename: string };

      const byteCharacters = atob(data.docx);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      saveAs(blob, data.filename);
    } catch (err: any) {
      console.error('DOCX export error:', err);
      showToast(err.message || 'Failed to export DOCX', 'error');
    } finally {
      setExporting(null);
    }
  };

  // Save a single check
  const handleSaveCheck = async (updated: EditCheckSpec) => {
    try {
      const checkRef = doc(
        dataManagementSubcollection(projectId, 'editCheckSpec', 'checks'),
        updated.id,
      );
      // Explicit allow-list of user-editable fields. The inline editor in
      // EditCheckDetail.tsx only exposes these six fields plus active (which
      // has its own toggle handler), so we MUST NOT spread the whole `updated`
      // object back to Firestore — doing so would clobber the structured
      // RTM fields (`crfFormId`, `crfQuestionIds`, `linkageStatus`,
      // `requirementIds`) that the Phase 2 generator wrote. Those fields
      // can't be edited via this UI and must be preserved across edits.
      // Form/field identity (crfForm / fieldName) is also intentionally
      // not editable here — to change which form/field a check targets,
      // delete the check and let the next regenerate produce a new one.
      await updateDoc(checkRef, {
        rule: updated.rule,
        technicalRule: updated.technicalRule,
        queryMessage: updated.queryMessage,
        severity: updated.severity,
        timing: updated.timing,
        checkType: updated.checkType,
        lastModifiedAt: Timestamp.now(),
      });
    } catch (err: any) {
      console.error('Error saving check:', err);
      showToast(err.message || 'Failed to save check', 'error');
    }
  };

  // Toggle active/inactive
  const handleToggleActive = async (checkId: string) => {
    const check = checks.find((c) => c.id === checkId);
    if (!check) return;

    try {
      const checkRef = doc(
        dataManagementSubcollection(projectId, 'editCheckSpec', 'checks'),
        checkId,
      );
      await updateDoc(checkRef, {
        active: !check.active,
        lastModifiedAt: Timestamp.now(),
      });

      // The summary Verification reads (J6) is recomputed from the checks
      // that exist, never patched field by field.
      const metaRef = dataManagementDoc(projectId, 'editCheckSpec');
      await updateDoc(metaRef, {
        ...specSummaryOf(checks.map((c) => (c.id === checkId ? { ...c, active: !c.active } : c))),
        lastModifiedAt: Timestamp.now(),
      });
    } catch (err: any) {
      console.error('Error toggling check:', err);
      showToast(err.message || 'Failed to update check', 'error');
    }
  };

  // Delete a check
  const handleDeleteCheck = async (checkId: string) => {
    try {
      const checkRef = doc(
        dataManagementSubcollection(projectId, 'editCheckSpec', 'checks'),
        checkId,
      );
      await deleteDoc(checkRef);

      // Same rule as the toggle: recompute, so `checkCount` and both
      // breakdowns still describe the checks that are actually there.
      const metaRef = dataManagementDoc(projectId, 'editCheckSpec');
      await updateDoc(metaRef, {
        ...specSummaryOf(checks.filter((c) => c.id !== checkId)),
        lastModifiedAt: Timestamp.now(),
      });
    } catch (err: any) {
      console.error('Error deleting check:', err);
      showToast(err.message || 'Failed to delete check', 'error');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Not yet generated
  if (!specData) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <p className="text-gray-500 mb-2">
            Generate a comprehensive Edit Check Specification based on your CRFs, protocol
            requirements, and data dictionary.
          </p>
          <p className="text-xs text-gray-400 mb-6">
            Checks are organized by category (endpoint, safety, protocol compliance, general) and
            include severity levels, timing, query messages, and technical rules. Requires CRFs to
            be created first.
          </p>

          {generating ? (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 text-sm text-blue-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                {generationProgress || 'Generating...'}
              </div>
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
                </div>
              </div>
              <p className="text-xs text-gray-400">
                This may take 2-3 minutes to analyze all forms and fields.
              </p>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium"
            >
              <Play className="h-4 w-4" />
              Generate Edit Check Specification
            </button>
          )}
        </div>
      </div>
    );
  }

  // Spec exists - show full UI
  return (
    <div className="space-y-3">
      {/* Status bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-500">
            Version <span className="font-medium text-gray-800">{specData.version}</span>
          </span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">
            Status{' '}
            <span className="font-medium text-gray-800 capitalize">{specData.status}</span>
          </span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">
            <span className="font-medium text-gray-800">{checks.length}</span> checks
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCsv}
            disabled={exporting !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {exporting === 'csv' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            Export CSV
          </button>
          <button
            onClick={handleExportDocx}
            disabled={exporting !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {exporting === 'docx' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileText className="h-3.5 w-3.5" />
            )}
            Export DOCX
          </button>
        </div>
      </div>

      {/* From the uploaded dictionary (D7) */}
      {specData.dictionaryChecks && (() => {
        const dict = specData.dictionaryChecks;
        const text = dictionarySummaryText(dict);
        return (
          <details
            open={dict.notWrittenCount > 0}
            className="px-4 py-2.5 bg-teal-50/60 border border-teal-200 rounded-lg text-sm"
          >
            <summary className="cursor-pointer font-medium text-teal-900">{text.headline}</summary>
            <div className="mt-2 space-y-2 text-gray-700">
              {text.replaced && (
                <div>
                  <p>{text.replaced}</p>
                  <ul className="mt-1 ml-4 list-disc text-xs text-gray-600 space-y-0.5">
                    {dict.replaced.map((r) => (
                      <li key={r.id}>
                        {r.crfForm} · {r.fieldName}: {r.rule}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {text.notWritten && (
                <div>
                  <p className="font-medium text-amber-800">{text.notWritten}</p>
                  {text.listCut && <p className="text-xs text-gray-500">{text.listCut}</p>}
                  <ul className="mt-1 ml-4 list-disc text-xs text-gray-700 space-y-0.5">
                    {dict.notWritten.map((n, i) => (
                      <li key={`${n.form}|${n.field}|${n.column}|${i}`}>{notWrittenLine(n)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </details>
        );
      })()}

      {/* Default plausibility ranges (sponsor to confirm) */}
      {defaultRangesText(specData.defaultRanges) && (
        <div className="px-4 py-2.5 bg-amber-50/70 border border-amber-200 rounded-lg text-sm text-amber-900">
          {defaultRangesText(specData.defaultRanges)}
        </div>
      )}

      {/* What was NOT written, and why (docgen ECS) */}
      {(() => {
        const nw = notWrittenSummary(specData);
        if (!nw.hasAny) return null;
        const gapGroups = splitFamilyGaps(specData.familyGaps);
        return (
          <details className="px-4 py-2.5 bg-amber-50/60 border border-amber-200 rounded-lg text-sm">
            <summary className="cursor-pointer font-medium text-amber-900">{nw.headline}</summary>
            <div className="mt-2 space-y-3 text-gray-700">
              {nw.rejected && (
                <div>
                  <p className="font-medium text-gray-800">Proposed checks not written</p>
                  <p className="text-xs text-gray-600">{nw.rejected}</p>
                  {nw.rejectedCut && <p className="text-xs text-gray-500">{nw.rejectedCut}</p>}
                  <ul className="mt-1 ml-4 list-disc text-xs text-gray-700 space-y-1">
                    {(specData.rejectedChecks ?? []).map((r, i) => {
                      const line = rejectedCheckLine(r);
                      return (
                        <li key={`${r.crfForm}|${r.fieldName}|${r.name}|${i}`}>
                          <span>{line.title}</span>
                          <ul className="ml-4 list-[circle] text-gray-600">
                            {line.reasons.map((reason, j) => (
                              <li key={j}>{reason}</li>
                            ))}
                          </ul>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {nw.gaps && (
                <div>
                  <p className="font-medium text-gray-800">Rule-based checks not written</p>
                  <p className="text-xs text-gray-600">{nw.gaps}</p>
                  <ul className="mt-1 ml-4 list-disc text-xs text-gray-700 space-y-0.5">
                    {gapGroups.notWritten.map((g, i) => (
                      <li key={`${g.family}|${i}`}>{familyGapLine(g)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {nw.notApplicable && (
                <div>
                  {/* When it is the only list, the summary line already says this. */}
                  {!nw.onlyNotApplicable && <p className="font-medium text-gray-800">Not applicable to this study</p>}
                  <p className="text-xs text-gray-600">{nw.notApplicable}</p>
                  <ul className="mt-1 ml-4 list-disc text-xs text-gray-500 space-y-0.5">
                    {gapGroups.notApplicable.map((g, i) => (
                      <li key={`${g.family}|${i}`}>{familyGapLine(g)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {nw.forms && (
                <div>
                  <p className="font-medium text-gray-800">Forms with no checks</p>
                  <p className="text-xs text-gray-600">{nw.forms}</p>
                  <ul className="mt-1 ml-4 list-disc text-xs text-gray-700 space-y-0.5">
                    {(specData.excludedForms ?? []).map((f, i) => (
                      <li key={`${f.crfFormId}|${i}`}>{excludedFormLine(f)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </details>
        );
      })()}

      {/* Filters */}
      <EditCheckFilters
        search={search}
        onSearchChange={setSearch}
        category={categoryFilter}
        onCategoryChange={setCategoryFilter}
        severity={severityFilter}
        onSeverityChange={setSeverityFilter}
        timing={timingFilter}
        onTimingChange={setTimingFilter}
        activeFilter={activeFilter}
        onActiveFilterChange={setActiveFilter}
        source={sourceFilter}
        onSourceChange={setSourceFilter}
        totalCount={checks.length}
        filteredCount={filteredChecks.length}
      />

      {/* Table */}
      <EditCheckTable
        checks={filteredChecks}
        onSaveCheck={handleSaveCheck}
        onToggleActive={handleToggleActive}
        onDeleteCheck={handleDeleteCheck}
      />

      {/* Regenerate */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            if (
              confirm(
                'Regenerate the entire edit check specification? Any manual edits will be overwritten.'
              )
            ) {
              handleGenerate();
            }
          }}
          disabled={generating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${generating ? 'animate-spin' : ''}`} />
          Regenerate All Checks
        </button>
      </div>
    </div>
  );
}
